import { Injectable } from '@angular/core';
import { List } from 'immutable';
import { Observable } from 'rxjs';
import { map, share } from 'rxjs/operators';
import {
  ACCEPT_VERSION_HEADER,
  AuthHttpService,
  ENTITY_ACCEPT_VERSION_HEADER,
} from '../auth-data/auth-http.service';
import { DOC_ACCEPT_VERSION } from '../document-data/document-data.data';
import { Document } from '../document-data/document.model';
import { IResponseInfo } from '../http/http.data';
import { IRendererOptions } from '../renderer/renderer.interface';
import {
  GUTENBERG_ACCEPT_VERSION,
  GUTENBERG_ACCEPT_VERSION_HEADER,
} from '../renderer/renderer.service';
import { PaymentsDataBuilder } from './payments-data.builder';
import {
  IAddPaymentPayload,
  IReceiptPreviewPayload,
  IRemindersPreview,
  ISendReceiptPayload,
  ISendReceiptResponse,
  ITransactionHistoryUrlResponse,
  PayPalTransactionItem,
} from './payments-data.data';
import { IFetchPayPalTransactionHistorySuccessPayload } from './payments-data.actions';
import { UrlService } from 'i2g-ng-utils';
import { NEXT_PAGE_HEADER } from '../entity-data/entity-data.data';

export const PAYMENT_RECEIPTS_ACCEPT_VERSION = '2.1.0';
export const STRIPE_ONBOARDING_URL_ACCEPT_VERSION = '1.0.0';
export const PAYPAL_PAYMENTS_ACCEPT_VERSION = '1.0.0';

@Injectable()
export class PaymentsDataService {
  private _paymentsUrl = '/payments';

  protected _paymentReceiptsHeaders: { [headerKey: string]: string } = {
    [ACCEPT_VERSION_HEADER]: PAYMENT_RECEIPTS_ACCEPT_VERSION,
    [ENTITY_ACCEPT_VERSION_HEADER]: DOC_ACCEPT_VERSION,
    [GUTENBERG_ACCEPT_VERSION_HEADER]: GUTENBERG_ACCEPT_VERSION,
  };
  protected inputHeaders: any = {
    [ACCEPT_VERSION_HEADER]: DOC_ACCEPT_VERSION,
  };
  protected stripeOnboardingHeaders: any = {
    [ACCEPT_VERSION_HEADER]: STRIPE_ONBOARDING_URL_ACCEPT_VERSION,
  };
  protected payPalHeaders: any = {
    [ACCEPT_VERSION_HEADER]: PAYPAL_PAYMENTS_ACCEPT_VERSION,
  };

  constructor(
    private _authHttpService: AuthHttpService,
    private _paymentsDataBuilder: PaymentsDataBuilder,
    private _urlService: UrlService
  ) {}

  sendRemindersPreview(reminderDays: List<number>) {
    const url = `${this._paymentsUrl}/reminders/preview`;
    const payload: IRemindersPreview = this._paymentsDataBuilder.buildRemindersPreviewPayload(
      reminderDays
    );

    return this._authHttpService.post(url, payload).pipe(share());
  }

  sendInvoiceUnopenedPreview() {
    const url = `${this._paymentsUrl}/reminders/invoice_unopened/preview`;

    return this._authHttpService.post(url).pipe(share());
  }

  sendReceipt(
    documentId: string,
    transactionId: string,
    rendererOptions: IRendererOptions
  ): Observable<ISendReceiptResponse> {
    const url = `${this._paymentsUrl}/receipts`;
    const payload: ISendReceiptPayload = this._paymentsDataBuilder.buildSendReceiptPayload(
      documentId,
      transactionId,
      rendererOptions
    );

    return this._authHttpService
      .post(url, payload, this._paymentReceiptsHeaders)
      .pipe(
        map(({ body, ..._ }: IResponseInfo) => body as ISendReceiptResponse),
        share()
      );
  }

  sendReceiptPreview(
    document: Document,
    transaction: IAddPaymentPayload,
    rendererOptions: IRendererOptions
  ): Observable<ISendReceiptResponse> {
    const url = `${this._paymentsUrl}/receipts/preview`;
    const payload: IReceiptPreviewPayload = this._paymentsDataBuilder.buildReceiptPreviewPayload(
      document,
      transaction,
      rendererOptions
    );

    return this._authHttpService
      .post(url, payload, this._paymentReceiptsHeaders)
      .pipe(
        map(({ body, ..._ }: IResponseInfo) => body as ISendReceiptResponse),
        share()
      );
  }

  /**
   * It will return the URL for users who have set up Stripe
   * (returns error 2005 if user has never enabled Stripe)
   */
  getStripeOnboardingUrl(returnUrl: string): Observable<string> {
    const url = `${this._paymentsUrl}/stripe/onboarding_url`;
    const params = {
      return_url: returnUrl,
    };

    return this._authHttpService
      .get(url, params, this.stripeOnboardingHeaders)
      .pipe(map(({ body, ..._ }: IResponseInfo) => body['url']));
  }

  addManualPayment(
    id: string,
    payload: IAddPaymentPayload
  ): Observable<IResponseInfo> {
    const url = `/invoices/${id}/payments/manual_transactions`;

    return this._authHttpService.post(url, payload, this.inputHeaders);
  }

  fetchTransactionDetailsUrl(
    invoiceId: string,
    transactionId: string
  ): Observable<ITransactionHistoryUrlResponse> {
    const url = `/invoices/${invoiceId}/payments/${transactionId}`;

    return this._authHttpService
      .get(url, {}, this.inputHeaders)
      .pipe(
        map(
          ({ body, ..._ }: IResponseInfo) =>
            body as ITransactionHistoryUrlResponse
        )
      );
  }

  fetchPayPalTransactionHistory(
    timestamp?: string
  ): Observable<IFetchPayPalTransactionHistorySuccessPayload> {
    const url = `/payments/paypal/transactions`;

    let params = {};

    if (timestamp) {
      params = { timestamp };
    }

    return this._authHttpService.get(url, params, this.payPalHeaders).pipe(
      map(({ body, headers, ..._ }: IResponseInfo) => {
        let nextPageTimestamp = null;

        if (headers.get(NEXT_PAGE_HEADER)) {
          nextPageTimestamp = this._urlService.parseExternalUrl(
            headers.get(NEXT_PAGE_HEADER)
          )?.query?.timestamp;
        }

        return {
          transactions: List<PayPalTransactionItem>(body['transactions']),
          nextPageTimestamp,
        };
      })
    );
  }
}
